import { useApolloClient, useMutation } from "@apollo/client";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { loginMutation } from "services/apollo/Mutations";
import axios from "axios";
import toast from "react-hot-toast";
import logger from "Utils/logger";
import { storage, KEYS } from "Utils/storage";

// Create the context with a default value of null
const AuthContext = createContext(null);

// AuthProvider component
export const AuthProvider = ({ children }) => {
    const pathname = window.location.pathname;
    const apolloClient = useApolloClient();
    const [isAuthLoading, setIsAuthLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [authLoader, setAuthLoader] = useState(false);
    const [stage, setStage] = useState('');
    const [email, setEmail] = useState('');
    const [, setOtp] = useState('');
    const [company_id, setCompany_id] = useState('');
    const [companies, setCompanies] = useState([]);
    const [password, setPassword] = useState('');
    const [locationData, setLocationData] = useState(null);
    const lastCompany = storage.get(KEYS.COMPANY_ID);
    const lastEmail = storage.get(KEYS.EMAIL);

    const geolocation = async () => {
        return new Promise((resolve, reject) => {
            axios.get("https://api.db-ip.com/v2/free/self")
                .then((response) => {
                    resolve(response.data);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    };

    const setAuthState = (isAuthenticated) => {
        storage.set(KEYS.IS_AUTHENTICATED, isAuthenticated);
        setIsAuthenticated(isAuthenticated);
    };

    useEffect(() => {
        const fetchLocation = async () => {
            try {
                const data = await geolocation();
                setLocationData(data);
            } catch (err) {
                logger.log(err.message || "Something went wrong");
            }
        };

        fetchLocation();
    }, []);

    const [loginApi] = useMutation(loginMutation, {
        onCompleted: async (data) => {
            try {
                const loginData = data?.login;
                if (!loginData) {
                    setAuthState(false);
                    toast.error('Login failed. Please try again.');
                    logger.log('🚀 ~ AuthProvider ~ empty login data:', data);
                    return;
                }

                if (loginData.status_code !== 4001) {
                    if (loginData.token) {
                        storage.set(KEYS.TOKEN, loginData.token);
                        storage.set(KEYS.REFRESH_TOKEN, loginData.refresh_token);
                        await apolloClient.resetStore();
                        setAuthState(true);
                        logger.log('Auth Provider to /connect');
                        window.location.href = '/connect';
                        return;
                    }

                    logger.log('🚀 ~ loginApi ~ data: 2', loginData);
                    setAuthState(false);
                    setCompanies(loginData.companies || []);
                    if (loginData.next_step) {
                        setStage(loginData.next_step);
                    }
                } else {
                    setAuthState(false);
                }

                if (loginData.message) {
                    if (loginData.status) {
                        toast.success(loginData.message);
                    } else {
                        toast.error(loginData.message);
                    }
                }
                logger.log('🚀 ~ AuthProvider ~ data:', data);
            } catch (err) {
                setAuthState(false);
                toast.error('Something went wrong. Please try again.');
                logger.log('🚀 ~ AuthProvider ~ onCompleted error:', err);
            }
        },
        onError: (error) => {
            setAuthState(false);
            logger.log('🚀 ~ AuthProvider ~ error:', error);
        },
    })

    const [session_token, setsession_token] = useState('');

    useEffect(() => {
        logger.log('🚀 ~ session_token changed:', session_token);
    }, [session_token])

    const login = useCallback(async (code = '', company_id = '') => {
        let xmpp_token = storage.get(KEYS.XMPP_TOKEN_ID)
        let json_data = { email: email, password: password, step: 'validate', device_type: 'web', device_id: xmpp_token, countryName: locationData?.countryName, city: locationData?.city, ipAddress: locationData?.ipAddress, time: new Date().toLocaleString() }
        if (code) {
            delete json_data['password']

            json_data['code'] = code
            json_data['step'] = 'otp'
            json_data['session_token'] = session_token
        } else if (company_id) {
            json_data['step'] = 'company'
            json_data['company_id'] = company_id
            json_data['session_token'] = session_token
        }

        if (pathname?.includes('/signin_with_otp') && !code && !company_id) {
            json_data['type'] = 'signin_with_otp'
        }

        try {
            logger.log('🚀 ~ login ~ json_data:', json_data);
            setAuthLoader(true)
            const result = await loginApi({
                variables: {
                    input: json_data
                }
            }).catch((err) => {
                logger.log('🚀 ~ login ~ mutation rejected:', err);
                return null;
            });
            const data = result?.data;
            if (company_id && !data?.login?.status) {
                setStage('');
            }
            if (data?.login?.session_token) {
                setsession_token(data?.login?.session_token)
            }
            setAuthLoader(false)
        } catch (error) {
            setAuthLoader(false)
            setAuthState(false);
            toast.error(error?.message || 'Login failed. Please try again.');
            logger.log("🚀 ~ login ~ error:", error)
        }
    }, [email, password, locationData, pathname, loginApi, session_token]);

    useEffect(() => {
        // Check localStorage for a saved authentication state

        const checkAuth = async () => {
            // Simulate loading user auth status (e.g., from localStorage or API)
            const savedAuthState = storage.get(KEYS.IS_AUTHENTICATED);
            if (savedAuthState) {
                setAuthState(JSON.parse(savedAuthState));
            } else {
                setAuthState(false);
            }
            setIsAuthLoading(false);
        };
        checkAuth();

    }, []);

    const logout = useCallback(() => {
        setAuthState(false);
        storage.remove(KEYS.IS_AUTHENTICATED);  // Remove saved authentication state
    }, []);

    const authValue = useMemo(() => ({
        isAuthenticated, isAuthLoading, authLoader, stage, login, logout,
        email, company_id, setCompany_id, setEmail, password, setPassword,
        setOtp, companies, lastCompany, lastEmail,
    }), [
        isAuthenticated, isAuthLoading, authLoader, stage, login, logout,
        email, company_id, setCompany_id, setEmail, password, setPassword,
        setOtp, companies, lastCompany, lastEmail,
    ]);

    return (
        <AuthContext.Provider value={authValue}>
            {children}
        </AuthContext.Provider>
    );
};

// Custom hook to use auth context
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
};
